import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import {
  Loader2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  AlertCircle, ThumbsUp, ThumbsDown, Info, MessageCircle, XCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import Header from './Header';
import MarkdownRenderer from '../../utils/MarkdownRenderer';

/* ───────────────── helpers ───────────────── */
const tsToMillis = (ts) => {
  if (!ts) return null;
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'string') {
    const ms = Date.parse(ts);
    return Number.isNaN(ms) ? null : ms;
  }
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts.seconds === 'number') {
    return ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1e6);
  }
  return null;
};

const getSessionStats = (responses) => {
  if (!Array.isArray(responses) || responses.length === 0) {
    return { total: 0, errors: 0, thumbsUp: 0, thumbsDown: 0 };
  }
  return responses.reduce(
    (acc, r) => ({
      total: acc.total + 1,
      errors: acc.errors + (r.response?.error ? 1 : 0),
      thumbsUp: acc.thumbsUp + (r.reaction === 'positive' ? 1 : 0),
      thumbsDown: acc.thumbsDown + (r.reaction === 'negative' ? 1 : 0),
    }),
    { total: 0, errors: 0, thumbsUp: 0, thumbsDown: 0 }
  );
};

const getSessionTimestamps = (responses = []) => {
  const ms = responses
    .map((r) => tsToMillis(r.timestamp))
    .filter((v) => v !== null);
  return {
    first: ms.length ? Math.min(...ms) : null,
    last: ms.length ? Math.max(...ms) : null,
  };
};

const formatTimestamp = (ts) => {
  const ms = tsToMillis(ts);
  if (!ms) return 'No timestamp';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

/* ────── helpers for date inputs ────── */
const toStartOfDay = (ymd) =>
  ymd ? new Date(`${ymd}T00:00:00`).getTime() : null;
const toEndOfDay = (ymd) =>
  ymd ? new Date(`${ymd}T23:59:59.999`).getTime() : null;

/* ───────────────── component ───────────────── */
const PAGE_SIZE = 20;

const LogsViewer = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [user, setUser] = useState(null);

  /* pagination state */
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(logs.length / PAGE_SIZE));
  const rangeStart = logs.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = logs.length ? Math.min(page * PAGE_SIZE, logs.length) : 0;
  const currentLogs = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* auth guard */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => (u ? setUser(u) : navigate('/login')));
    return unsub;
  }, [navigate]);

  /* fetch logs */
  useEffect(() => {
    (async () => {
      try {
        const qs = await getDocs(collection(db, 'logs'));
        const data = qs.docs.map((d) => {
          const obj = { id: d.id, ...d.data() };
          obj.lastTimestamp = getSessionTimestamps(obj.responses).last;
          return obj;
        });
        data.sort((a, b) => (b.lastTimestamp ?? 0) - (a.lastTimestamp ?? 0));
        setLogs(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* handlers */
  const handleLogout = () =>
    signOut(auth).then(() => navigate('/login')).catch(console.error);

  const toggleDetails = (id) =>
    setExpandedLog((prev) => (prev === id ? null : id));

  const toggleTechnicalDetails = (mid, e) => {
    e.stopPropagation();
    setExpandedDetails((prev) => ({ ...prev, [mid]: !prev[mid] }));
  };

  /**
   * Export helper – if `range` is omitted the whole dataset is exported.
   * @param {{from?:number,to?:number}=} range  epoch-ms range (inclusive)
   */
  const downloadLogsAsCSV = (range = {}) => {
    const { from, to } = range;

    const filtered = logs.filter((log) =>
      (log.responses || []).some((r) => {
        const ms = tsToMillis(r.timestamp);
        return (
          (from == null || ms >= from) &&
          (to == null || ms <= to)
        );
      })
    );

    if (!filtered.length) return;

    const rows = [
      [
        'Session ID',
        'Timestamp',
        'Message #',
        'User Query',
        'Answer',
        'Error',
        'Reaction',
      ],
    ];

    filtered.forEach((log) => {
      const ts = formatTimestamp(log.lastTimestamp);
      (log.responses || []).forEach((r, idx) => {
        rows.push([
          log.id,
          ts,
          r.msgSeq ?? idx,
          JSON.stringify(r?.response?.user_query || ''),
          JSON.stringify(r?.response?.answer || ''),
          JSON.stringify(r?.response?.error || ''),
          r.reaction || '',
        ]);
      });
    });

    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `chatbot_logs_${new Date().toISOString()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* LogEntry sub-component */
  const LogEntry = ({ response, index }) => {
    const mid = `${response.msgSeq}-${index}`;
    const showTech = expandedDetails[mid];
    const hasError = !!response.response?.error;

    const borderColor =
      response.reaction === 'positive'
        ? 'border-green-500'
        : response.reaction === 'negative'
        ? 'border-red-500'
        : 'border-blue-500';

    const renderContext = (ctx) => {
      if (!ctx || hasError) return null;
      try {
        const arr = Array.isArray(ctx) ? ctx : [ctx];
        return (
          <div className="space-y-4">
            <p className="text-gray-600 font-medium text-base mb-2 text-left">
              Used Context:
            </p>
            {arr.map((pair, i) => {
              const [qPart, ...aParts] = pair.split('\nAnswer:');
              const q = qPart.replace('Question:', '').trim();
              const a = aParts.join('\nAnswer:').trim();
              return (
                <div key={i} className="flex flex-col space-y-2">
                  <div className="bg-blue-50 p-3 rounded border border-blue-200">
                    <p className="font-medium text-blue-800 mb-1">Question:</p>
                    <MarkdownRenderer>{q}</MarkdownRenderer>
                  </div>
                  {a && (
                    <div className="bg-white p-3 rounded border border-gray-200">
                      <p className="font-medium text-gray-700 mb-1">Answer:</p>
                      <MarkdownRenderer>{a}</MarkdownRenderer>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      } catch (e) {
        console.error('Context render error', e);
        return null;
      }
    };

    return (
      <div className={`border-l-4 ${borderColor} pl-4 mb-4`}>
        {/* header row */}
        <div className="flex justify-between items-start mb-2">
          <div>
            <span className="font-semibold text-lg">
              Message {response.msgSeq}
            </span>
            <span className="ml-4 text-sm text-gray-500">
              {formatTimestamp(response.timestamp)}
            </span>
          </div>
          {!hasError && (
            <button
              onClick={(e) => toggleTechnicalDetails(mid, e)}
              className="text-gray-500 hover:text-gray-700"
            >
              <Info className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* content */}
        <div className="space-y-2">
          {/* user query */}
          <div className="bg-gray-50 p-3 rounded">
            <p className="font-bold text-xl text-gray-600 mb-1">User Query</p>
            <p className="text-sm text-left">
              {response.response?.user_query || 'No query available'}
            </p>
          </div>

          {/* answer or error */}
          {hasError ? (
            <div className="bg-red-50 p-3 rounded flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
              <div>
                <p className="font-medium text-sm text-red-700 mb-1">Error</p>
                <p className="text-sm text-red-600">
                  {response.response.error}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white p-3 rounded border border-gray-200">
              <p className="font-bold text-xl text-gray-600 mb-1">Response</p>
              <div className="prose prose-sm max-w-none text-left">
                <MarkdownRenderer>
                  {response.response?.answer || ''}
                </MarkdownRenderer>
              </div>
            </div>
          )}

          {/* technical details */}
          {showTech && !hasError && (
            <div className="bg-gray-50 p-3 rounded mt-2 text-sm">
              <h4 className="font-bold text-xl mb-3 text-center">
                Technical Details
              </h4>

              {Array.isArray(response.response?.reframed_questions) &&
                response.response.reframed_questions.length > 0 && (
                  <div className="mb-4">
                    <p className="font-medium text-gray-600 mb-2">
                      Reframed Questions:
                    </p>
                    <p className="ml-5 space-y-1 text-gray-700">
                      {response.response.reframed_questions.map((q, i) => (
                        <li key={i}>{q.replace(/^\d+\.\s*/, '')}</li>
                      ))}
                    </p>
                  </div>
                )}

              {renderContext(response.response?.context)}
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ───────────────── render ───────────────── */
  return (
    <div className="flex flex-col h-screen">
      <Header user={user} onLogout={handleLogout} />

      <div className="flex-1 overflow-auto bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* title */}
          <h1 className="text-2xl font-bold mb-1">System Logs</h1>

          {/* arrows */}
          {logs.length > PAGE_SIZE && (
            <div className="flex justify-left items-center gap-2 mb-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* range + CSV */}
          <div className="flex flex-wrap gap-4 items-end mb-6">
            {/* <span className="text-sm text-gray-500">
              {logs.length > 0 && `${rangeStart} – ${rangeEnd} of ${logs.length}`}
            </span> */}

            {/* date pickers */}
            <div className="flex items-center gap-2">
              <label className="text-sm">From:</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="text-sm border rounded px-2 py-1"
              />
              <label className="text-sm">To:</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="text-sm border rounded px-2 py-1"
              />
            </div>

            {/* download buttons */}
            <div className="flex gap-2">

              <button
                className="text-sm px-4 py-1.5 border rounded bg-white hover:bg-gray-100"
                disabled={!fromDate && !toDate}
                onClick={() =>
                  downloadLogsAsCSV({
                    from: toStartOfDay(fromDate),
                    to: toEndOfDay(toDate),
                  })
                }
              >
                Download Range CSV
              </button>
              <button
                className="text-sm px-4 py-1.5 border rounded bg-white hover:bg-gray-100"
                onClick={() => downloadLogsAsCSV()}
              >
                Download ALL CSV
              </button>
            </div>
          </div>

          {/* main content */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-red-700">
              <p className="font-medium">Error loading logs</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {currentLogs.map((log) => {
                const stats = getSessionStats(log.responses);
                const sessionTime = log.lastTimestamp
                  ? formatTimestamp(log.lastTimestamp)
                  : 'No timestamp';

                return (
                  <div key={log.id} className="bg-white rounded-lg shadow-sm">
                    <div
                      className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleDetails(log.id)}
                    >
                      <div>
                        <h3 className="font-medium">Session ID: {log.id}</h3>
                        <span className="text-sm text-gray-500 ml-2">
                          {sessionTime}
                        </span>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-4 h-4" />
                            {stats.total}
                          </span>
                          <span className="flex items-center gap-1">
                            <XCircle className="w-4 h-4 text-red-500" />
                            {stats.errors}
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsUp className="w-4 h-4 text-green-500" />
                            {stats.thumbsUp}
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsDown className="w-4 h-4 text-red-500" />
                            {stats.thumbsDown}
                          </span>
                        </div>
                      </div>
                      {expandedLog === log.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>

                    {expandedLog === log.id && (
                      <div className="p-4 border-t border-gray-100">
                        {(log.responses || []).map((r, i) => (
                          <LogEntry key={i} response={r} index={i} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogsViewer;
